import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

@Component({
  selector: 'app-bulk-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './bulk-create-rate-modal.component.html',
  styleUrl: './bulk-create-rate-modal.component.scss',
})
export class BulkCreateRateModalComponent implements OnInit {
  rateForm!: FormGroup;
  isSubmitting = false;

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  categoryTypeArray: Array<any> = [];
  selectedCategoryID!: number;
  selectedCategory: string = '';

  currentStep: number = 1;

  isSaveButtonEnabled: boolean = false;

  isDragOver = false;
  uploadedFile: File | null = null;

  isPreviewed = false;
  previewData: any = null;
  activeTab: 'errors' | 'warnings' = 'errors';

  // New
  uploadSuccess = false;
  uploadSuccessStats: any = null;
  uploadHardError = false;
  fileError: string | null = null;

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private activeModal = inject(NgbActiveModal);
  modalService = inject(NgbModal);
  rateManagerService = inject(RateManagerService);
  fb = inject(FormBuilder);

  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      file: [null, Validators.required],
    });

    this.getCategory();
  }

  closeModal = () => {
    this.modalService.dismissAll();
  };

  async getCategory() {
    try {
      const response =
        await this.rateManagerService.getProviderRatesCategories();
      const mapped = response.data.map((item: any) => ({
        value: item.id,
        label: item.name
          ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
          : item.name,
        description: item.description,
      }));

      this.categoryTypeArray = mapped;

      return mapped;
    } catch (error) {
      console.error('Error fetching category types:', error);
      this.categoryTypeArray = [];
    }
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;

    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.label,
    });
  };

  goToPrevStep = () => {
    if (this.isPreviewed) {
      this.isPreviewed = false;
      this.previewData = null;
      this.activeTab = 'errors';
      this.uploadedFile = null;
      this.uploadHardError = false;
      this.uploadSuccess = false;
      this.uploadSuccessStats = null;
      this.fileError = null;
      this.rateForm.patchValue({ file: null });
      this.resetFileInput();
      return;
    }

    if (this.currentStep > 1) {
      this.currentStep--;

      if (this.currentStep === 1) {
        this.uploadedFile = null;
        this.rateForm.patchValue({ file: null });
        this.resetFileInput();
      }
    }

    // Disable save button on step 1
    this.isSaveButtonEnabled = this.currentStep > 1;
  };

  private resetFileInput() {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  clearFile() {
    this.uploadedFile = null;
    this.fileError = null;
    this.resetFileInput();
  }

  closeAndReset() {
    this.resetUploadState();
    this.activeModal.close('success');
  }

  goToNextStep = async () => {
    this.currentStep++;

    switch (this.currentStep) {
      case 2:
        if (!this.selectedCategoryID) return;

        this.isSaveButtonEnabled = true;
        break;

      default:
        break;
    }
  };

  handleBulkUpload() {
    if (!this.uploadedFile) {
      this.fileError = 'Please select a CSV file before submitting.';
      return;
    }

    this.fileError = null;
    this.rateForm.patchValue({ file: this.uploadedFile });
    this.submitBulkRate();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    this.handleFile(file);
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      this.fileError = 'Only CSV files are allowed.';
      return;
    }

    if (file.size === 0) {
      this.fileError = 'File is empty — please select a valid CSV.';
      return;
    }

    this.fileError = null;
    this.uploadedFile = file;
  }

  async submitBulkRate() {
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      await AlertUtil.error('Please fill all required fields.');
      return;
    }

    this.isSubmitting = true;

    const buildFormData = (preview: boolean): FormData => {
      const formData = new FormData();
      formData.append('rate_category_id', this.rateForm.get('rate_category_id')?.value);
      formData.append('rate_category_name', this.rateForm.get('rate_category_name')?.value);
      formData.append('file', this.uploadedFile!);
      formData.append('preview', String(preview));
      formData.append('status', 'in_review');
      return formData;
    };

    try {
      if (!this.isPreviewed) {
        // Step 1: Preview — validate without saving. HTTP errors are caught separately
        // so that dialog handling below never falls into the HTTP error handler.
        let response: any;
        try {
          response = await firstValueFrom(this.rateManagerService.bulkCreateRate(buildFormData(true)));
        } catch (err: any) {
          this.handleUploadError(err);
          return;
        }

        this.previewData = response.data;
        this.isPreviewed = true;
        this.uploadHardError = false;

        const hasErrors = (this.previewData?.errors?.length || 0) > 0;
        const hasWarnings = (this.previewData?.warnings?.length || 0) > 0;
        this.activeTab = hasErrors ? 'errors' : (hasWarnings ? 'warnings' : 'errors');
      } else {
        // Step 2: Actual submit
        this.uploadHardError = false;
        let response: any;
        try {
          response = await firstValueFrom(this.rateManagerService.bulkCreateRate(buildFormData(false)));
        } catch (err: any) {
          this.handleUploadError(err);
          return;
        }

        this.uploadSuccessStats = response.data;
        this.uploadSuccess = true;
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleUploadError(err: any) {
    console.error('Bulk upload error:', err);

    const errorBody = err?.error;
    if (errorBody?.error && typeof errorBody.error === 'object') {
      // Structured error — transition to step 3 and render inline
      this.previewData = errorBody.error;
      this.isPreviewed = true;
      this.uploadHardError = true;
      this.activeTab = 'errors';
    } else {
      // Unstructured error — keep user on current step, simple toast
      const specificError = typeof errorBody?.error === 'string' ? errorBody.error : null;
      const msg = specificError || errorBody?.message || err?.message || 'Failed to process file.';
      AlertUtil.error(msg);
    }
  }

  private resetUploadState() {
    this.rateForm.reset();
    this.uploadedFile = null;
    this.isPreviewed = false;
    this.previewData = null;
    this.activeTab = 'errors';
    this.currentStep = 1;
    this.selectedCategoryID = undefined!;
    this.selectedCategory = '';
    this.isSaveButtonEnabled = false;
    this.uploadSuccess = false;
    this.uploadSuccessStats = null;
    this.uploadHardError = false;
    this.fileError = null;
    this.resetFileInput();
  }

  setActiveTab(tab: 'errors' | 'warnings') {
    this.activeTab = tab;
  }

  downloadTemplate = () => {
    const selectedCategoryId = this.selectedCategoryID;
    const selectedCategory = this.selectedCategory;

    this.rateManagerService.downloadRateTemplate(selectedCategoryId).subscribe({
      next: (csvText: string) => {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:T]/g, '');
        link.download = `template_${selectedCategory}.csv`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      },
      error: (err: any) => {
        console.error('Failed to download CSV:', err);
      },
    });
  };
}
